<script setup lang="ts">
import { VForm } from 'vuetify/components/VForm'
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import authV2LoginIllustrationBorderedDark from '@images/pages/auth-v2-login-illustration-bordered-dark.png'
import authV2LoginIllustrationBorderedLight from '@images/pages/auth-v2-login-illustration-bordered-light.png'
import authV2LoginIllustrationDark from '@images/pages/auth-v2-login-illustration-dark.png'
import authV2LoginIllustrationLight from '@images/pages/auth-v2-login-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { useAuthStore } from '@/stores/auth'
import { ability } from '@/plugins/casl'
import { post } from '@/api'

const authThemeImg = useGenerateImageVariant(
  authV2LoginIllustrationLight,
  authV2LoginIllustrationDark,
  authV2LoginIllustrationBorderedLight,
  authV2LoginIllustrationBorderedDark,
  true,
)

const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: false, // 로그인 여부와 상관없이 접근 가능
  },
})

const authStore = useAuthStore()
const route = useRoute()

// OAuth 파라미터
const clientId = computed(() => route.query.client_id as string || '')
const redirectUri = computed(() => route.query.redirect_uri as string || '')
const scope = computed(() => route.query.scope as string || '*')
const state = computed(() => route.query.state as string || '')

// 상태
const isPasswordVisible = ref(false)
const isLoading = ref(false)
const isAuthorizing = ref(false)
const errorMessage = ref('')

const errors = ref<Record<string, string | undefined>>({
  email: undefined,
  password: undefined,
})

const refVForm = ref<VForm>()

const credentials = ref({
  email: '',
  password: '',
})

// OAuth 파라미터 유효성 검사
const isValidOAuthParams = computed(() => {
  return !!(clientId.value && redirectUri.value)
})

// 클라이언트 이름 (추후 API로 조회 가능)
const clientName = computed(() => {
  if (clientId.value.startsWith('regle_'))
    return 'Regle MCP Server'

  return 'External Application'
})

// Auth Code 생성 및 리다이렉트
const authorizeAndRedirect = async () => {
  if (!authStore.isLoggedIn) {
    errorMessage.value = '로그인이 필요합니다.'

    return
  }

  isAuthorizing.value = true
  errorMessage.value = ''

  try {
    // 인증된 사용자용 API 호출 (Sanctum 인증 사용)
    const response = await post('/oauth/authorize', {
      client_id: clientId.value,
      redirect_uri: redirectUri.value,
      scope: scope.value,
    })

    if (response.success && response.data?.code) {
      // redirect_uri로 auth code와 state 전달
      const redirectUrl = new URL(redirectUri.value)

      redirectUrl.searchParams.set('code', response.data.code)
      if (state.value)
        redirectUrl.searchParams.set('state', state.value)

      // 외부 URL로 리다이렉트
      window.location.href = redirectUrl.toString()
    }
    else {
      errorMessage.value = response.error?.message || '인증 코드 생성에 실패했습니다.'
    }
  }
  catch (err: unknown) {
    console.error('OAuth 인증 오류:', err)
    errorMessage.value = err instanceof Error ? err.message : '인증 처리 중 오류가 발생했습니다.'
  }
  finally {
    isAuthorizing.value = false
  }
}

// 로그인 후 자동 인증
const login = async () => {
  isLoading.value = true
  errors.value = { email: undefined, password: undefined }
  errorMessage.value = ''

  try {
    const result = await authStore.login(credentials.value.email, credentials.value.password)

    if (result.success && result.user) {
      // 권한 업데이트
      const userAbilities = result.user.ability || [{ action: 'read', subject: 'Auth' }]

      ability.update(userAbilities)

      // 로그인 성공 후 바로 인증 진행
      await authorizeAndRedirect()
    }
    else {
      errors.value.email = '이메일 또는 비밀번호가 올바르지 않습니다.'
    }
  }
  catch (err) {
    console.error('로그인 오류:', err)
    errors.value.email = '로그인 중 오류가 발생했습니다.'
  }
  finally {
    isLoading.value = false
  }
}

const onSubmit = () => {
  refVForm.value?.validate()
    .then(({ valid: isValid }) => {
      if (isValid)
        login()
    })
}

// 인증 거부
const denyAuthorization = () => {
  if (redirectUri.value) {
    const redirectUrl = new URL(redirectUri.value)

    redirectUrl.searchParams.set('error', 'access_denied')
    redirectUrl.searchParams.set('error_description', 'User denied the authorization request')
    if (state.value)
      redirectUrl.searchParams.set('state', state.value)

    window.location.href = redirectUrl.toString()
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        SV Groupware
      </h1>
    </div>
  </RouterLink>

  <VRow
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="d-flex align-center justify-center w-100 h-100"
          style="padding-inline: 6.25rem;"
        >
          <VImg
            max-width="613"
            :src="authThemeImg"
            class="auth-illustration mt-16 mb-2"
          />
        </div>

        <img
          class="auth-footer-mask"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <!-- OAuth 파라미터 오류 -->
        <template v-if="!isValidOAuthParams">
          <VCardText>
            <VAlert
              type="error"
              variant="tonal"
              class="mb-4"
            >
              <VAlertTitle>잘못된 OAuth 요청</VAlertTitle>
              필수 파라미터(client_id, redirect_uri)가 누락되었습니다.
            </VAlert>
          </VCardText>
        </template>

        <!-- 이미 로그인된 경우: 동의 화면 -->
        <template v-else-if="authStore.isLoggedIn">
          <VCardText>
            <h4 class="text-h4 mb-1">
              앱 연동 승인
            </h4>
            <p class="mb-0">
              {{ clientName }}에서 귀하의 계정에 접근하려고 합니다.
            </p>
          </VCardText>

          <VCardText>
            <VAlert
              type="info"
              variant="tonal"
              class="mb-4"
            >
              <div class="d-flex align-center gap-2 mb-2">
                <VIcon icon="tabler-user" />
                <span class="font-weight-medium">{{ authStore.userData?.name }}</span>
              </div>
              <div class="text-body-2">
                {{ authStore.userData?.email }}
              </div>
            </VAlert>

            <div class="mb-4">
              <div class="text-body-1 font-weight-medium mb-2">
                요청 권한:
              </div>
              <VChip
                size="small"
                color="primary"
                variant="tonal"
                class="me-2"
              >
                {{ scope || '*' }}
              </VChip>
            </div>

            <VAlert
              v-if="errorMessage"
              type="error"
              variant="tonal"
              class="mb-4"
            >
              {{ errorMessage }}
            </VAlert>

            <div class="d-flex gap-3">
              <VBtn
                color="primary"
                :loading="isAuthorizing"
                :disabled="isAuthorizing"
                @click="authorizeAndRedirect"
              >
                승인
              </VBtn>
              <VBtn
                variant="outlined"
                :disabled="isAuthorizing"
                @click="denyAuthorization"
              >
                거부
              </VBtn>
            </div>

            <VDivider class="my-6" />

            <p class="text-body-2 text-medium-emphasis">
              다른 계정으로 로그인하시겠습니까?
              <a
                href="#"
                class="text-primary"
                @click.prevent="authStore.logout()"
              >
                로그아웃
              </a>
            </p>
          </VCardText>
        </template>

        <!-- 로그인 필요 -->
        <template v-else>
          <VCardText>
            <h4 class="text-h4 mb-1">
              앱 연동을 위한 로그인
            </h4>
            <p class="mb-0">
              {{ clientName }}과 연동하려면 먼저 로그인하세요.
            </p>
          </VCardText>

          <VCardText>
            <VForm
              ref="refVForm"
              @submit.prevent="onSubmit"
            >
              <VRow>
                <!-- email -->
                <VCol cols="12">
                  <AppTextField
                    v-model="credentials.email"
                    label="이메일"
                    placeholder="user@seoulventures.net"
                    type="email"
                    autofocus
                    :rules="[requiredValidator, emailValidator]"
                    :error-messages="errors.email"
                  />
                </VCol>

                <!-- password -->
                <VCol cols="12">
                  <AppTextField
                    v-model="credentials.password"
                    label="비밀번호"
                    placeholder="············"
                    :rules="[requiredValidator]"
                    :type="isPasswordVisible ? 'text' : 'password'"
                    autocomplete="current-password"
                    :error-messages="errors.password"
                    :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                    @click:append-inner="isPasswordVisible = !isPasswordVisible"
                  />

                  <div class="my-6" />

                  <VBtn
                    block
                    type="submit"
                    :loading="isLoading"
                    :disabled="isLoading"
                  >
                    로그인 후 연동
                  </VBtn>

                  <div class="d-flex align-center my-6">
                    <VDivider />
                    <span class="mx-4 text-high-emphasis">또는</span>
                    <VDivider />
                  </div>

                  <!-- 소셜 로그인 -->
                  <VBtn
                    block
                    color="#dd4b39"
                    :href="`/auth/google/redirect?oauth_callback=${encodeURIComponent(route.fullPath)}`"
                    class="text-white"
                  >
                    <VIcon
                      start
                      icon="tabler-brand-google"
                    />
                    Google 계정으로 로그인
                  </VBtn>
                </VCol>
              </VRow>
            </VForm>
          </VCardText>
        </template>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth";
</style>
